// Service hours per property — F&B opening times across the Grup Xaluca collection.
// Each entry: a property + the list of food & beverage services it offers,
// with optional venue name and a short note (seasonal variation, dress code, etc.).
//
// Hours are local Moroccan time (Africa/Casablanca, GMT+1 in winter).
// All labels are trilingual (en / fr / es) and rendered via the pick(obj, lang) helper.

const L = {
  // Common service-kind labels (reused across properties)
  breakfast: { en: "Breakfast", fr: "Petit-déjeuner", es: "Desayuno" },
  lunch_pool: { en: "Lunch · Poolside", fr: "Déjeuner · Piscine", es: "Almuerzo · Piscina" },
  lunch_lake: { en: "Lunch · Lakeside", fr: "Déjeuner · Au bord du lac", es: "Almuerzo · A orillas del lago" },
  lunch: { en: "Lunch", fr: "Déjeuner", es: "Almuerzo" },
  dinner_buffet: { en: "Dinner buffet", fr: "Buffet du soir", es: "Buffet de la noche" },
  dinner_a_la_carte: { en: "À la carte dinner", fr: "Dîner à la carte", es: "Cena a la carta" },
  bar: { en: "Bars", fr: "Bars", es: "Bares" },
  fire_circle: { en: "Fire-circle dinner", fr: "Dîner cercle de feu", es: "Cena círculo de fuego" },
  high_tea: { en: "Tea time · Atlas", fr: "L'heure du thé · Atlas", es: "La hora del té · Atlas" },
  picnic: { en: "Trail picnic", fr: "Pique-nique randonneur", es: "Picnic de senderismo" },
};

export const PROPERTY_SERVICE_HOURS = [
  // ── Kasbahs & Hotels ──────────────────────────────────────────────────
  {
    slug: "kasbah-hotel-xaluca-arfoud",
    category: "hotel",
    name: { en: "Kasbah Hotel Xaluca · Arfoud", fr: "Kasbah Hotel Xaluca · Arfoud", es: "Kasbah Hotel Xaluca · Arfoud" },
    location: { en: "Arfoud · Tafilalet", fr: "Arfoud · Tafilalet", es: "Arfoud · Tafilalet" },
    services: [
      { kind: "breakfast", label: L.breakfast, hours: "06:30 – 10:30", venue: { en: "Sijilmassa Restaurant", fr: "Restaurant Sijilmassa", es: "Restaurante Sijilmassa" } },
      { kind: "lunch_pool", label: L.lunch_pool, hours: "12:30 – 15:30", venue: { en: "Pool deck · Bladi", fr: "Pont piscine · Bladi", es: "Terraza piscina · Bladi" } },
      { kind: "dinner_buffet", label: L.dinner_buffet, hours: "19:30 – 22:30", venue: { en: "Tizimi · Moroccan dining room", fr: "Tizimi · Salle marocaine", es: "Tizimi · Salón marroquí" } },
      { kind: "dinner_a_la_carte", label: L.dinner_a_la_carte, hours: "19:30 – 22:00", venue: { en: "Mabroka · on reservation", fr: "Mabroka · sur réservation", es: "Mabroka · bajo reserva" } },
      { kind: "bar", label: L.bar, hours: "11:00 – 01:00", venue: { en: "Tataoui Bar · Sahara Bar", fr: "Tataoui Bar · Sahara Bar", es: "Tataoui Bar · Sahara Bar" } },
    ],
    note: { en: "Mabroka private dining room (120 covers) bookable for groups and weddings.", fr: "Salon privé Mabroka (120 couverts) réservable pour groupes et mariages.", es: "Salón privado Mabroka (120 cubiertos) reservable para grupos y bodas." },
  },
  {
    slug: "kasbah-hotel-tombouctou",
    category: "hotel",
    name: { en: "Kasbah Hotel Tombouctou", fr: "Kasbah Hotel Tombouctou", es: "Kasbah Hotel Tombouctou" },
    location: { en: "Merzouga · Erg Chebbi", fr: "Merzouga · Erg Chebbi", es: "Merzouga · Erg Chebbi" },
    services: [
      { kind: "breakfast", label: L.breakfast, hours: "06:30 – 10:30" },
      { kind: "lunch_pool", label: L.lunch_pool, hours: "12:30 – 15:00" },
      { kind: "dinner_buffet", label: L.dinner_buffet, hours: "19:30 – 22:30", venue: { en: "Caravane dining room", fr: "Salle Caravane", es: "Salón Caravane" } },
      { kind: "dinner_a_la_carte", label: L.dinner_a_la_carte, hours: "20:00 – 22:00", venue: { en: "Rooftop · sunset menu", fr: "Toit-terrasse · menu coucher de soleil", es: "Azotea · menú atardecer" }, note: { en: "Best 30 min before sunset", fr: "Idéal 30 min avant le coucher", es: "Ideal 30 min antes del atardecer" } },
      { kind: "bar", label: L.bar, hours: "11:00 – 00:30" },
    ],
  },
  {
    slug: "hotel-xaluca-dades",
    category: "hotel",
    name: { en: "Hotel Xaluca Dades", fr: "Hotel Xaluca Dades", es: "Hotel Xaluca Dades" },
    location: { en: "Boumalne Dades · Atlas foothills", fr: "Boumalne Dades · Contreforts de l'Atlas", es: "Boumalne Dades · Estribaciones del Atlas" },
    services: [
      { kind: "breakfast", label: L.breakfast, hours: "07:00 – 10:30" },
      { kind: "lunch_pool", label: L.lunch_pool, hours: "12:30 – 15:00", venue: { en: "Garden terrace", fr: "Terrasse du jardin", es: "Terraza del jardín" } },
      { kind: "dinner_buffet", label: L.dinner_buffet, hours: "19:30 – 22:30" },
      { kind: "dinner_a_la_carte", label: L.dinner_a_la_carte, hours: "19:30 – 22:00", venue: { en: "Gorge-view dining room", fr: "Salle vue gorge", es: "Salón vista al cañón" } },
      { kind: "bar", label: L.bar, hours: "11:00 – 00:30", venue: { en: "Panorama Bar", fr: "Panorama Bar", es: "Panorama Bar" } },
    ],
  },
  {
    slug: "hotel-riad-xaluca",
    category: "hotel",
    name: { en: "Hotel Riad Xaluca · Merzouga", fr: "Hotel Riad Xaluca · Merzouga", es: "Hotel Riad Xaluca · Merzouga" },
    location: { en: "Merzouga · doorstep of the dunes", fr: "Merzouga · au seuil des dunes", es: "Merzouga · al borde de las dunas" },
    services: [
      { kind: "breakfast", label: L.breakfast, hours: "06:30 – 10:30", venue: { en: "Riad courtyard", fr: "Patio du riad", es: "Patio del riad" } },
      { kind: "lunch_pool", label: L.lunch_pool, hours: "12:30 – 15:30", venue: { en: "Infinity pool deck", fr: "Pont piscine à débordement", es: "Terraza piscina infinita" } },
      { kind: "dinner_a_la_carte", label: L.dinner_a_la_carte, hours: "19:30 – 22:30", venue: { en: "Panoramic restaurant", fr: "Restaurant panoramique", es: "Restaurante panorámico" } },
      { kind: "bar", label: L.bar, hours: "11:00 – 01:00", venue: { en: "Rooftop bar · over the dunes", fr: "Bar du toit · sur les dunes", es: "Bar de la azotea · sobre las dunas" } },
    ],
    note: { en: "Sunrise breakfast served on the rooftop on request.", fr: "Petit-déjeuner au lever du soleil servi sur le toit sur demande.", es: "Desayuno al amanecer en la azotea bajo petición." },
  },
  {
    slug: "hotel-xaluca-spa-aguelmame-sidi-ali",
    category: "hotel",
    name: { en: "Hotel Xaluca Spa Aguelmame Sidi Ali", fr: "Hotel Xaluca Spa Aguelmame Sidi Ali", es: "Hotel Xaluca Spa Aguelmame Sidi Ali" },
    location: { en: "Lake Sidi Ali · 2 100 m", fr: "Lac Sidi Ali · 2 100 m", es: "Lago Sidi Ali · 2 100 m" },
    services: [
      { kind: "breakfast", label: L.breakfast, hours: "07:30 – 10:30", venue: { en: "Lakeside dining room", fr: "Salle au bord du lac", es: "Salón a orillas del lago" } },
      { kind: "lunch_lake", label: L.lunch_lake, hours: "12:30 – 15:30", venue: { en: "Trout & cedar · 120 covers", fr: "Truite & cèdre · 120 couverts", es: "Trucha y cedro · 120 cubiertos" } },
      { kind: "dinner_a_la_carte", label: L.dinner_a_la_carte, hours: "19:30 – 22:00", venue: { en: "Cedar dining room", fr: "Salle en cèdre", es: "Salón de cedro" } },
      { kind: "bar", label: L.bar, hours: "11:00 – 00:00", venue: { en: "Fireplace bar", fr: "Bar à cheminée", es: "Bar con chimenea" } },
      { kind: "high_tea", label: L.high_tea, hours: "16:00 – 18:00", venue: { en: "Lobby fireplace", fr: "Cheminée du lobby", es: "Chimenea del vestíbulo" }, note: { en: "Cedar-smoked tea + honey cakes", fr: "Thé fumé au cèdre + gâteaux au miel", es: "Té ahumado al cedro + pasteles de miel" } },
    ],
  },
  {
    slug: "xaluca-ait-ben-haddou",
    category: "hotel",
    name: { en: "Xaluca Aït Ben Haddou Maison d'Hôte", fr: "Xaluca Aït Ben Haddou Maison d'Hôte", es: "Xaluca Aït Ben Haddou Maison d'Hôte" },
    location: { en: "Aït Ben Haddou · UNESCO ksar", fr: "Aït Ben Haddou · Ksar UNESCO", es: "Aït Ben Haddou · Ksar UNESCO" },
    services: [
      { kind: "breakfast", label: L.breakfast, hours: "07:00 – 10:30", venue: { en: "Inner courtyard", fr: "Patio intérieur", es: "Patio interior" } },
      { kind: "lunch", label: L.lunch, hours: "13:00 – 15:00", venue: { en: "Ksar terrace", fr: "Terrasse du ksar", es: "Terraza del ksar" } },
      { kind: "dinner_a_la_carte", label: L.dinner_a_la_carte, hours: "19:30 – 22:00", venue: { en: "Family table · 14 covers", fr: "Table d'hôtes · 14 couverts", es: "Mesa familiar · 14 cubiertos" }, note: { en: "Single seating · on reservation", fr: "Service unique · sur réservation", es: "Servicio único · bajo reserva" } },
    ],
  },
  {
    slug: "hotel-xaluca-midelt",
    category: "hotel",
    name: { en: "Hotel Xaluca Midelt", fr: "Hotel Xaluca Midelt", es: "Hotel Xaluca Midelt" },
    location: { en: "Midelt · Cirque de Jaffar", fr: "Midelt · Cirque de Jaffar", es: "Midelt · Circo de Jaffar" },
    services: [
      { kind: "breakfast", label: L.breakfast, hours: "07:00 – 10:00" },
      { kind: "lunch", label: L.lunch, hours: "12:30 – 15:00", venue: { en: "Orchard pavilion", fr: "Pavillon du verger", es: "Pabellón del huerto" } },
      { kind: "dinner_buffet", label: L.dinner_buffet, hours: "19:30 – 22:00", venue: { en: "Mountain dining room", fr: "Salle montagne", es: "Salón de montaña" } },
      { kind: "bar", label: L.bar, hours: "11:00 – 23:30" },
    ],
    note: { en: "Opening 2026 — provisional hours, subject to refinement.", fr: "Ouverture 2026 — horaires provisoires, susceptibles d'évoluer.", es: "Apertura 2026 — horarios provisionales, sujetos a cambios." },
  },

  // ── Bivouacs & Camps ──────────────────────────────────────────────────
  {
    slug: "auberge-la-belle-etoile",
    category: "bivouac",
    name: { en: "Auberge La Belle Étoile", fr: "Auberge La Belle Étoile", es: "Auberge La Belle Étoile" },
    location: { en: "Foot of Erg Chebbi · Merzouga", fr: "Pied de l'Erg Chebbi · Merzouga", es: "Pie del Erg Chebbi · Merzouga" },
    services: [
      { kind: "breakfast", label: L.breakfast, hours: "06:30 – 10:00", venue: { en: "Terrace facing the dunes", fr: "Terrasse face aux dunes", es: "Terraza frente a las dunas" } },
      { kind: "lunch", label: L.lunch, hours: "12:30 – 15:00", venue: { en: "Garden under the tamarisks", fr: "Jardin sous les tamaris", es: "Jardín bajo los tamariscos" } },
      { kind: "dinner_a_la_carte", label: L.dinner_a_la_carte, hours: "19:30 – 22:00", venue: { en: "Berber dining room", fr: "Salle berbère", es: "Salón bereber" }, note: { en: "Music nights · Thu / Sat 21:00", fr: "Soirées musique · jeu / sam 21 h", es: "Noches musicales · jue/sáb 21:00" } },
    ],
  },
  {
    slug: "la-belle-etoile-bivouacs",
    category: "bivouac",
    name: { en: "La Belle Étoile Luxury Desert Camp", fr: "La Belle Étoile Luxury Desert Camp", es: "La Belle Étoile Luxury Desert Camp" },
    location: { en: "Heart of Erg Chebbi", fr: "Cœur de l'Erg Chebbi", es: "Corazón del Erg Chebbi" },
    services: [
      { kind: "breakfast", label: L.breakfast, hours: "07:00 – 10:00", venue: { en: "Sunrise table on the dune", fr: "Table du lever sur la dune", es: "Mesa al amanecer en la duna" } },
      { kind: "picnic", label: L.picnic, hours: "On request · 24h prior", venue: { en: "Sandboarding · quad routes", fr: "Sandboard · circuits quad", es: "Sandboard · rutas quad" } },
      { kind: "fire_circle", label: L.fire_circle, hours: "19:30 – 22:30", venue: { en: "5-course tasting · sommelier", fr: "Dégustation 5 services · sommelier", es: "Degustación 5 tiempos · sumiller" }, note: { en: "Smart-casual · stargazing follows", fr: "Tenue chic-décontractée · observation des étoiles", es: "Smart casual · observación de estrellas" } },
      { kind: "bar", label: L.bar, hours: "17:00 – 01:00", venue: { en: "Open bar in the lounge tent", fr: "Bar ouvert sous la tente salon", es: "Barra abierta en tienda salón" } },
    ],
  },
  {
    slug: "kamkamia-luxury-desert-camp",
    category: "bivouac",
    name: { en: "Kamkamia Luxury Desert Camp", fr: "Kamkamia Luxury Desert Camp", es: "Kamkamia Luxury Desert Camp" },
    location: { en: "South dunes · Merzouga", fr: "Dunes sud · Merzouga", es: "Dunas sur · Merzouga" },
    services: [
      { kind: "breakfast", label: L.breakfast, hours: "07:00 – 10:00", venue: { en: "Communal tent", fr: "Tente commune", es: "Tienda comunal" } },
      { kind: "fire_circle", label: L.fire_circle, hours: "19:30 – 22:00", venue: { en: "Berber tagine · 3 services", fr: "Tagine berbère · 3 services", es: "Tajín bereber · 3 tiempos" } },
      { kind: "bar", label: L.bar, hours: "18:00 – 00:30" },
    ],
  },
  {
    slug: "merdani-bivouac",
    category: "bivouac",
    name: { en: "Merdani Bivouac", fr: "Bivouac Merdani", es: "Vivac Merdani" },
    location: { en: "North-east dunes · Erg Chebbi", fr: "Dunes nord-est · Erg Chebbi", es: "Dunas noreste · Erg Chebbi" },
    services: [
      { kind: "breakfast", label: L.breakfast, hours: "06:45 – 09:30" },
      { kind: "fire_circle", label: L.fire_circle, hours: "20:00 – 22:00", venue: { en: "Candle-lit haima", fr: "Haïma aux chandelles", es: "Jaima a la luz de velas" }, note: { en: "Gnawa music on Saturdays", fr: "Musique gnawa le samedi", es: "Música gnawa los sábados" } },
    ],
  },

  // ── Refuge ───────────────────────────────────────────────────────────
  {
    slug: "xaluca-ifess-fess-refuge",
    category: "refuge",
    name: { en: "Xaluca Ifess Fess Refuge de Montagne", fr: "Refuge Xaluca Ifess Fess", es: "Refugio Xaluca Ifess Fess" },
    location: { en: "M'Goun Massif · Atlas", fr: "Massif du M'Goun · Atlas", es: "Macizo del M'Goun · Atlas" },
    services: [
      { kind: "breakfast", label: L.breakfast, hours: "06:30 – 09:00", venue: { en: "Wood-fire room", fr: "Salle au feu de bois", es: "Sala con fuego de leña" } },
      { kind: "picnic", label: L.picnic, hours: "Packed at 06:30", venue: { en: "Bread · cheese · olives · dates", fr: "Pain · fromage · olives · dattes", es: "Pan · queso · aceitunas · dátiles" } },
      { kind: "high_tea", label: L.high_tea, hours: "17:00 – 18:30", venue: { en: "By the cedar fire", fr: "Au coin du feu de cèdre", es: "Junto al fuego de cedro" } },
      { kind: "dinner_a_la_carte", label: L.dinner_a_la_carte, hours: "19:30 – 21:30", venue: { en: "Amazigh table · 14 covers", fr: "Table amazighe · 14 couverts", es: "Mesa amazigh · 14 cubiertos" }, note: { en: "Single seating · communal", fr: "Service unique · communautaire", es: "Servicio único · comunitario" } },
    ],
  },
];

// Filter pills: by property family
export const HOURS_FAMILIES = [
  { id: "all", label: { en: "All properties", fr: "Toutes les adresses", es: "Todas las direcciones" } },
  { id: "hotel", label: { en: "Kasbahs & hotels", fr: "Kasbahs & hôtels", es: "Kasbahs y hoteles" } },
  { id: "bivouac", label: { en: "Bivouacs & camps", fr: "Bivouacs & camps", es: "Vivacs y campamentos" } },
  { id: "refuge", label: { en: "Refuge", fr: "Refuge", es: "Refugio" } },
];

// Service-kind → icon glyph (lucide-react names)
export const SERVICE_ICON = {
  breakfast: "Coffee",
  lunch: "UtensilsCrossed",
  lunch_pool: "Waves",
  lunch_lake: "Waves",
  dinner_buffet: "UtensilsCrossed",
  dinner_a_la_carte: "UtensilsCrossed",
  bar: "Wine",
  fire_circle: "Flame",
  high_tea: "Coffee",
  picnic: "Backpack",
};
